import { getRandomCharacter, isInValidIdForSoc2 } from "@/modules/agent/components/customize-controls/helpers"; // Import getRandomCharacter and isInValidIdForSoc2
import { useGetControlSetsQuery } from "@/modules/agent/requests";
import { getAgentStateActions, useAgentSessionData, useAgentStepData, useReassessmentModeStore } from "@/modules/agent/states";
import {
  getCustomizeControlsActions,
  isSoc2ControlValid,
  isVendorAssessmentControlValid,
  Soc2Control,
  TSCABBRIVATIONS,
  useCustomizeControlsStore,
  VendorAssessmentControl,
} from "@/modules/agent/states/cusomize-controls";
import { handleContinueCustomizeControls } from "@/modules/agent/use-cases/customize-control.use-case";
import { convertSnakeToTitleCase } from "@/modules/agent/utils/snake-to-capital";
import { AgentSessionType } from "@/modules/sessions/types";
import { ZButton } from "@/shared/components/button";
import { addNotification } from "@/shared/states/notification";
import { Loader, Upload, MoreHorizontal } from "lucide-react";
import { NistCheckboxContext } from "../../CustomizeControlsTable";
import BulkUploadDocumentsModal from "../../../customize-controls/BulkUploadDocumentsModal";
import { Dropdown, DropdownMenu, DropdownTrigger, DropdownItem } from "@nextui-org/react"; // Removed Tooltip
import ImportModal from "../../../customize-controls/ImportModal";
import { getAgentSession } from "@/modules/sessions/requests";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip"; // Added ZTooltip import
import { UploadedFile } from "@/shared/types/file-upload";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent } from "react"; // Added useMemo
import { useNavigate, useParams } from "react-router-dom";
import { saveAsDefaultControls, saveControlSet } from "../../../../api/controls";
import SaveControlsModal from "../../../customize-controls/SaveControlsModal";

const CustomizeControlTopRIghtSection = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stepData = useAgentStepData(sessionId || "");
  const sessionData = useAgentSessionData(sessionId || "");
  const { showCheckboxes: showNistCheckboxes, setShowCheckboxes: setShowNistCheckboxes } = useContext(NistCheckboxContext);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isSaveControlsModalOpen, setIsSaveControlsModalOpen] = useState(false);
  const [currentControlsUrl, setCurrentControlsUrl] = useState<string | null>(null);
  const [selectedControlSetNames, setSelectedControlSetNames] = useState<string[]>(() => {
    // Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(`selectedControlSetNames_${sessionId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const selectedControlSetNamesRef = useRef<string[]>([]);

  // Initialize ref from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`selectedControlSetNames_${sessionId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          selectedControlSetNamesRef.current = parsed;
        }
      }
    } catch (error) {
      console.error('Error initializing selectedControlSetNamesRef from localStorage:', error);
    }
  }, [sessionId]);

  // Save to localStorage whenever selectedControlSetNames changes
  useEffect(() => {
    localStorage.setItem(`selectedControlSetNames_${sessionId}`, JSON.stringify(selectedControlSetNames));
    // Also update the ref to keep it in sync
    selectedControlSetNamesRef.current = selectedControlSetNames;
  }, [selectedControlSetNames, sessionId]);

  // Re-load from localStorage when sessionId changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`selectedControlSetNames_${sessionId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSelectedControlSetNames(parsed);
          selectedControlSetNamesRef.current = parsed;
        }
      }
    } catch (error) {
      console.error('Error loading selectedControlSetNames from localStorage:', error);
    }
  }, [sessionId]);
  
  // Add reassessment mode check
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const isCurrentSessionInReassessment = isReassessmentMode && sessionId === reassessmentAgentId;
  
  const isSavingControls = useCustomizeControlsStore().isSavingControls;
  const selectedControls = useCustomizeControlsStore().selectedControls;
  const searchQuery = useCustomizeControlsStore().searchQuery;
  const selectedTab = useCustomizeControlsStore().selectedTab;
  const agentType = useCustomizeControlsStore().agentType;
  const showCheckboxes = useCustomizeControlsStore().showCheckboxes;
  const activeBulkAction = useCustomizeControlsStore().activeBulkAction;
  const newControlPlaceHolderUid = useCustomizeControlsStore()
    .newControlPlaceHolderUid;
  const noOfGenerateWithAiPopoverOpens = useCustomizeControlsStore()
    .noOfGenerateWithAiPopoverOpens;
  const isCustomControls = useCustomizeControlsStore().isCustomControls;
  const dataState = useCustomizeControlsStore().dataState; // Added dataState
  const isSearchActive = searchQuery.trim().length > 0;
  const isAddControlDisabled = isCurrentSessionInReassessment || isSearchActive;

  // Fetch existing control sets for multi-vendor, but only when import modal needs to be opened
  // Set isEnabled to false to prevent automatic API calls on component render
  const { data: existingControlSets = [], refetch: refetchControlSets } = useGetControlSetsQuery({ isEnabled: false });

  const handleNavigateToOverwrite = useCallback((name: string) => {
    // Navigate to overwrite page with the control set name as a query parameter
    navigate(`/settings/control-sets/overwrite?name=${encodeURIComponent(name)}`);
  }, [navigate]);

  // Function to extract controls URL from step data
  const extractControlsUrl = useCallback(() => {
    const controlSelectionStep = stepData?.find(
      (step) => step.type === "control_selection"
    );

    let controlsUrl: string | null = null;

    if (controlSelectionStep?.data) {
      const data = controlSelectionStep.data as any;
      controlsUrl = data.url;

      if (!controlsUrl && data.metadata?.control_sets && data.metadata.control_sets.length > 0) {
        controlsUrl = data.url;
      }
    }

    return controlsUrl;
  }, [stepData]);

  // Check if controls URL exists - needed to determine if Add Control button should be shown
  const hasControlsUrl = !!extractControlsUrl();

  // Handler to open the save controls modal
  const handleOpenSaveControlsModal = useCallback(() => {
    const controlsUrl = extractControlsUrl();
    setCurrentControlsUrl(controlsUrl);
    setIsSaveControlsModalOpen(true);
  }, [extractControlsUrl]);

  // Clear localStorage when save modal closes
  const handleCloseSaveControlsModal = useCallback(() => {
    setIsSaveControlsModalOpen(false);
    // Don't clear the stored names when just closing the modal
    // Only clear after actually saving
  }, []);

  // Calculate anyControlInvalid and isDuplicateId for non-NIST types
  const { anyControlInvalid, isDuplicateId } = useMemo(() => {
    let anyInvalid = false;
    let duplicateId = false;

    if (agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      anyInvalid = dataState.filter((control) => {
        const soc2Control = control as Soc2Control;
        return (
          soc2Control.tsc_id?.replace(
            TSCABBRIVATIONS[soc2Control.tsc],
            ""
          ).length > 0 &&
          isInValidIdForSoc2(
            soc2Control.tsc_id.replace(
              TSCABBRIVATIONS[soc2Control.tsc],
              ""
            )
          )
        );
      }).length > 0;

      duplicateId = new Set(dataState.map(c => (c as Soc2Control).tsc_id)).size !== dataState.length;
    } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      // Remove duplicate validation for multi-vendor assessment
      duplicateId = false;
    }
    return { anyControlInvalid: anyInvalid, isDuplicateId: duplicateId };
  }, [dataState, agentType]);

  const handleOnClickDeleteButtom = useCallback(() => {
    getCustomizeControlsActions().setDataState((prev) => {
      let newValue = [...prev];

      newValue = newValue.filter((v) => selectedControls.has(v.uid) === false);

      getCustomizeControlsActions().setSelectedControls(new Set<string>());
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
      
      getCustomizeControlsActions().setShowCheckboxes(false);
      
      
      addNotification({
        message: `${selectedControls.size} control${
          selectedControls.size > 1 ? "s" : ""
        } deleted.`,
        type: "success",
      });
      return newValue;
    });
  }, [selectedControls, selectedTab, agentType]);

  const handleOnClickAddButton = useCallback(() => {
    // For agent types with persistent empty rows, just focus on the existing empty row
    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ||
        agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      
      setTimeout(() => {
        const table = document.getElementById("customize-control-table");
        if (table) {
          // For SOC2 types, look for the tsc_id input in the empty row
          if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
            // Find all inputs with IDs containing "tsc_id"
            const tscIdInputs = table.querySelectorAll('input[id*="tsc_id"]');
            let targetInput: HTMLElement | null = null;
            
            // Look for the empty tsc_id input (should be empty or contain only the abbreviation)
            for (let input of tscIdInputs) {
              const inputEl = input as HTMLInputElement;
              const value = inputEl.value.trim();
              
              // Check if this is an empty tsc_id field or only contains abbreviation (CC, A, C, PI, P)
              if (value === "" || value === "CC" || value === "A" || value === "C" || value === "PI" || value === "P") {
                // Verify this is in an empty row by checking if this row has empty control and category
                const row = inputEl.closest('tr');
                if (row) {
                  const controlInput = row.querySelector('input[id*="control"], textarea[id*="control"]') as HTMLInputElement;
                  const categoryInput = row.querySelector('input[id*="category"], select[id*="category"]') as HTMLInputElement;
                  
                  if (controlInput && categoryInput && 
                      controlInput.value.trim() === "" && 
                      (categoryInput.value.trim() === "" || categoryInput.value.trim() === "Select Category")) {
                    targetInput = inputEl;
                    break;
                  }
                }
              }
            }
            
            if (targetInput) {
              targetInput.focus();
              targetInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
              return;
            }
          }
          
          // For MULTI_FILE_VENDOR_ASSESSMENT, look for category input
          if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const categoryInputs = table.querySelectorAll('input[id*="key"]');
            let targetInput: HTMLElement | null = null;
            
            for (let input of categoryInputs) {
              const inputEl = input as HTMLInputElement;
              if (inputEl.value === "" || inputEl.value.trim() === "") {
                // Check if this input is in a row that appears to be empty
                const row = inputEl.closest('tr');
                if (row) {
                  const questionInput = row.querySelector('textarea[id*="question"]') as HTMLTextAreaElement;
                  
                  if (questionInput && questionInput.value.trim() === "") {
                    targetInput = inputEl;
                    break;
                  }
                }
              }
            }
            
            if (targetInput) {
              targetInput.focus();
              targetInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
              return;
            }
          }
          
          // Fallback: find the last row and focus on the FIRST input in that row (not last input in table)
          const rows = table.querySelectorAll("tbody tr");
          if (rows.length > 0) {
            const lastRow = rows[rows.length - 1];
            
            // For SOC2 types, look for tsc_id input first
            if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
              const tscIdInput = lastRow.querySelector('input[id*="tsc_id"]') as HTMLElement;
              if (tscIdInput) {
                tscIdInput.focus();
                tscIdInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
                return;
              }
            }
            
            // For vendor assessment, look for category input first
            if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
              const categoryInput = lastRow.querySelector('input[id*="key"]') as HTMLElement;
              if (categoryInput) {
                categoryInput.focus();
                categoryInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
                return;
              }
            }
            
            // Generic fallback: first input/textarea in the last row
            const firstInput = lastRow.querySelector("input, textarea") as HTMLElement;
            if (firstInput) {
              firstInput.focus();
              firstInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }
        }
      }, 100);
      return;
    }

    // For other agent types (like SOC2_GAP), use the existing placeholder mechanism
    const uid = getRandomCharacter();
    getCustomizeControlsActions().setNewControlPlaceHolderUid(uid);
    
    setTimeout(() => {
      const table = document.getElementById("customize-control-table");
      if (table) {
        // For gap assessments, look for empty tsc_id input (first column for these agent types)
        if (agentType === AgentSessionType.SOC2_GAP) {
          const elementId = `${uid}tsc_id`;
          const targetInput = document.getElementById(elementId) as HTMLElement;
          
          if (targetInput) {
            targetInput.focus();
            targetInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
            return;
          }
        }
        
        // Fallback: find the last row and focus on first input
        const rows = table.querySelectorAll("tbody tr");
        if (rows.length > 0) {
          const lastRow = rows[rows.length - 1];
          const firstInput = lastRow.querySelector("input, textarea") as HTMLElement;
          if (firstInput) {
            firstInput.focus();
            firstInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      }
    }, 150);
  }, [agentType]);

  const handleAddControlClick = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      if (isAddControlDisabled) {
        event?.preventDefault();
        event?.stopPropagation();
        return;
      }

      handleOnClickAddButton();
    },
    [handleOnClickAddButton, isAddControlDisabled]
  );

  const toggleCheckboxes = useCallback(() => {
    getCustomizeControlsActions().setShowCheckboxes(!showCheckboxes);
    if (showCheckboxes) {
      // Clear selection when hiding checkboxes
      getCustomizeControlsActions().setSelectedControls(new Set());
    }
  }, [showCheckboxes]);

  const handleOpenBulkUploadModal = useCallback(() => {
    setIsBulkUploadModalOpen(true);
  }, []);

  const handleCloseBulkUploadModal = useCallback(() => {
    setIsBulkUploadModalOpen(false);
    getCustomizeControlsActions().setSelectedControls(new Set());
    getCustomizeControlsActions().setShowCheckboxes(false);
  }, []);

  const handleEnableCheckboxes = useCallback(() => {
    setShowNistCheckboxes(true);
  }, [setShowNistCheckboxes]);

  const handleAfterUpload = useCallback(() => {
    // Force an update to ensure data is refreshed after upload
    getCustomizeControlsActions().setChangesMade(prev => prev + 1);
    setIsBulkUploadModalOpen(false);
    // Reset checkbox mode after upload
    setShowNistCheckboxes(false);
    getCustomizeControlsActions().setShowCheckboxes(false);
  }, [setShowNistCheckboxes]);

  const handleOpenImportModal = useCallback(() => {
    // Only fetch control sets when import modal is opened and it's a multi-file vendor assessment
    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      refetchControlSets();
    }
    setIsImportModalOpen(true);
  }, [agentType, refetchControlSets]);

  const handleCloseImportModal = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const handleImportSuccess = useCallback(async (files?: UploadedFile[], controlsData?: any[], signedUrl?: string, selectedControlSetNames?: string[]) => {
    try {
      if (!sessionId) return;
      
      // Clear existing selected control set names when importing
      setSelectedControlSetNames([]);
      selectedControlSetNamesRef.current = [];
      
      // Store selected control set names for pre-populating save modal
      if (selectedControlSetNames && selectedControlSetNames.length > 0) {
        setSelectedControlSetNames(selectedControlSetNames);
        selectedControlSetNamesRef.current = selectedControlSetNames;
      }
      
      // Fetch fresh session data from API
      const sessionResponse = await getAgentSession(sessionId);
      
      if (sessionResponse.data) {
        // Update the store with fresh session data
        const { updateAgentSessionData, updateAgentStepData } = getAgentStateActions();
        updateAgentSessionData(sessionId, sessionResponse.data.session);
        updateAgentStepData(sessionId, sessionResponse.data.steps);
        
        // Use the existing continue function to refresh controls
        await handleContinueCustomizeControls(
          sessionResponse.data.steps, 
          sessionResponse.data.session
        );
      }
      
      // Force an update to ensure data is refreshed after import
      getCustomizeControlsActions().setChangesMade(prev => prev + 1);
      setIsImportModalOpen(false);
      
      // No notification - refresh happens silently in background
    } catch (error) {
      console.error("Error refreshing controls data:", error);

    }
  }, [sessionId]);

  const handleSaveAsDefault = useCallback(async () => {
    if (!sessionId) {
      addNotification({
        message: "Session ID not found",
        type: "error",
      });
      return;
    }
    
    try {
      // Map agent type to API parameter
      let templateType = "soc2_type2"; // default
      if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
        templateType = "soc2_type2"; // Both type 1 and type 2 use soc2_type2
      } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        templateType = "multi_file_vendor_assessment";
      }

      // Extract controls URL from step data
      const controlSelectionStep = stepData?.find(
        (step) => step.type === "control_selection"
      );
      
      let controlsUrl: string | null = null;
      
      if (controlSelectionStep?.data) {
        const data = controlSelectionStep.data as any; // Type assertion to handle union type
        controlsUrl = data.url;
        
        // If url is null and metadata contains control_sets, use the controls_url from there
        if (!controlsUrl && data.metadata?.control_sets && data.metadata.control_sets.length > 0) {
          controlsUrl = data.url;
        }
      }
      
      if (!controlsUrl) {
        addNotification({
          message: "Controls URL not found in step data",
          type: "error",
        });
        return;
      }

      await saveAsDefaultControls(templateType, controlsUrl, sessionId);
      
      addNotification({
        title: "Controls Saved Successfully",
        message: "All new vendor assessments will be conducted using these controls in your organization.",
        type: "success",
      });
    } catch (error) {
      addNotification({
        message: "Failed to save controls as default template",
        type: "error",
      });
      console.error("Save as default error:", error);
    }
  }, [agentType, sessionId, stepData]);

  const handleSaveControlSet = useCallback(async (name: string, overrideExisting?: boolean) => {
    if (!sessionId) {
      addNotification({
        message: "Session ID not found",
        type: "error",
      });
      return;
    }
    
    try {
      // Extract controls URL from step data
      const controlsUrl = extractControlsUrl();
      
      if (!controlsUrl) {
        addNotification({
          message: "Controls URL not found in step data",
          type: "error",
        });
        return;
      }
      
      // Store the controls URL for the modal
      setCurrentControlsUrl(controlsUrl);

      await saveControlSet(name, controlsUrl, sessionId);
      
      addNotification({
        message: `Control set '${name}' saved successfully!`,
        type: "success",
      });
      
      // Close the modal and clear stored names
      setIsSaveControlsModalOpen(false);
      localStorage.removeItem(`selectedControlSetNames_${sessionId}`);
    } catch (error) {
      console.error("Save control set error:", error);
      // Re-throw the error so the modal can handle it
      throw error;
    }
  }, [sessionId, extractControlsUrl]);

  if (!agentType) {
    return null;
  }

  const bulkUploadForSoc2 = () => {
    if (!showCheckboxes) {
      getCustomizeControlsActions().setActiveBulkAction("upload")
      toggleCheckboxes();
    }
  }
  return (
    <>
      {isSavingControls === "SAVING" && (
        <span className="flex items-center min-w-fit gap-1">
          <Loader className="w-4 h-4 animate-spin" />
          <p className="text-tiny text-[#A5A5A5] text-[8px]">Saving</p>
        </span>
      )}

      {noOfGenerateWithAiPopoverOpens === 0 && agentType === AgentSessionType.RISK_ASSESSMENT ? (
        showNistCheckboxes ? (
          selectedControls.size > 0 ? (
            <>
              <ZButton
                color="primary"
                variant="bordered"
                className="text-xs font-medium max-h-7 max-w-32 border-1 mr-2"
                onClick={handleOpenBulkUploadModal}
              >
                Add Documents
              </ZButton>
              {agentType !== AgentSessionType.RISK_ASSESSMENT && <ZButton
                color="danger"
                variant="solid"
                className="text-xs font-medium max-h-7 max-w-32 border-1"
                onClick={handleOnClickDeleteButtom}
                onMouseDown={handleOnClickDeleteButtom}
              >
                Delete Control{selectedControls.size > 1 ? "s" : ""}
              </ZButton>}
              <BulkUploadDocumentsModal
                isOpen={isBulkUploadModalOpen}
                onClose={handleCloseBulkUploadModal}
                onAfterUpload={handleAfterUpload}
                selectedControls={selectedControls}

              />
            </>
          ) : (
             <ZButton
              color="primary"
              variant="bordered"
              className="text-xs font-medium max-h-7 max-w-32 border-1"
              isDisabled={true}
              onClick={() => {
                setShowNistCheckboxes(false);
                getCustomizeControlsActions().setSelectedControls(new Set<string>());
              }}
            >
              Add Documents
            </ZButton>
          )
        ) : (
           (
            <ZButton
              color="primary"
              variant="bordered"
              className="text-xs font-medium max-h-7 max-w-42 border-1 flex items-center gap-1"
              onClick={handleEnableCheckboxes}
            >
             
              Bulk Upload
            </ZButton>
          )
        )
      ) : noOfGenerateWithAiPopoverOpens === 0 && (
        showCheckboxes ? (
          activeBulkAction === "upload" ? 
          <ZButton
              color="primary"
              variant="bordered"
              className="text-xs font-medium max-h-7 max-w-42 border-1 flex items-center gap-1"
              onClick={handleOpenBulkUploadModal}
              isDisabled={!selectedControls.size}
            >
             
              Add Documents
            </ZButton> : 
          <ZButton
            color="danger"
            variant="solid"
            className="text-xs font-medium max-h-7 max-w-32 border-1"
            onClick={handleOnClickDeleteButtom}
            onMouseDown={handleOnClickDeleteButtom}
            isDisabled={selectedControls.size === 0}
          >
            Delete Control{selectedControls.size > 1 ? "s" : ""}
          </ZButton>
        ) : (
          !showCheckboxes && noOfGenerateWithAiPopoverOpens === 0 && (
            <>
            {!isCustomControls ? (
                <>
                  <ZButton
                    color="primary"
                    variant="bordered"
                    className={`text-xs font-medium max-h-7 max-w-42 border-1 flex items-center gap-1 ${searchQuery.trim().length > 0 ? 'cursor-not-allowed' : ''}`}
                    onClick={bulkUploadForSoc2}
                    isDisabled={searchQuery.trim().length > 0}
                  >
                    Bulk Upload
                  </ZButton>
                </>
              ) : (<>
              {hasControlsUrl && (
                <ZButton
                  color="primary"
                  variant="bordered"
                  className={`text-xs font-medium max-h-7 max-w-32 border-1 ${isAddControlDisabled ? 'cursor-not-allowed' : ''}`}
                  onClick={handleAddControlClick}
                  onMouseDown={handleAddControlClick}
                >
                  Add Control
                </ZButton>
              )}
              {(agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT || 
                agentType === AgentSessionType.SOC2_GAP ||
                agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
                agentType === AgentSessionType.SOC2_GAP_TYPE2) && (
                <Dropdown 
                  placement="bottom-end"
                  classNames={{
                    content: "min-w-[140px] p-0 rounded-md"
                  }}
                  isDisabled={searchQuery.trim().length > 0}
                >
                  <DropdownTrigger>
                    <div className={`p-1 ml-1.5 rounded-full ${searchQuery.trim().length > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-100'}`}>
                      <MoreHorizontal className="h-4 w-4 text-gray-600" />
                    </div>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Control Actions"
                    className="p-0 rounded-sm"
                    itemClasses={{
                      base: "py-2.5 px-3 text-sm",
                      description: "text-foreground",
                      title: "text-foreground",
                    }}
                  >
                    {(() => {
                      const items = [];
                      
                      // Check if we're in reassessment mode for SOC1/SOC2 agent types
                      const isSOC1OrSOC2Agent = agentType === AgentSessionType.SOC2_GAP ||
                                                agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
                                                agentType === AgentSessionType.SOC2_GAP_TYPE2;
                      const isReassessmentForSOC = isCurrentSessionInReassessment && isSOC1OrSOC2Agent;
                      
                      // Add import item for all supported agent types (disabled in SOC reassessment)
                      items.push(
                        <DropdownItem 
                          key="import" 
                          className="text-xs rounded-none" 
                          classNames={{
                            base: `data-[hover=true]:bg-gray-100 rounded-none ${
                              isReassessmentForSOC ? "cursor-not-allowed opacity-50" : ""
                            }`,
                          }}
                          isDisabled={isReassessmentForSOC}
                          onClick={isReassessmentForSOC ? undefined : handleOpenImportModal}
                        >
                          <div>{agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ? "Import Controls" : "Import Controls from File"}</div>
                        </DropdownItem>
                      );

                      // Add save as default item disable if validation errors exist OR in SOC reassessment
                      const isSaveAsDefaultDisabled = (() => {
                        // Disable in SOC reassessment mode
                        if (isReassessmentForSOC) {
                          return true;
                        }
                        
                        // Disable if no controls exist (for all agent types)
                        if (!dataState || dataState.length === 0) {
                          return true;
                        }
                        
                        return (
                          !!dataState.filter((d) => {
                            if (
                              agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
                              agentType === AgentSessionType.SOC2_GAP ||
                              agentType === AgentSessionType.SOC2_GAP_TYPE2
                            ) {
                              return !isSoc2ControlValid(d as Soc2Control);
                            }
                            if (
                              agentType ===
                              AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
                            ) {
                              return !isVendorAssessmentControlValid(d as VendorAssessmentControl);
                            }
                            return false; 
                          }).length ||
                          isSavingControls === "SAVING" ||
                          isSavingControls === "EDITED" ||
                          anyControlInvalid ||
                          isDuplicateId
                        );
                      })();

                      const saveAsDefaultItemContent = (
                        <div className={isSaveAsDefaultDisabled ? `cursor-default w-full` : `w-full`} style={{ pointerEvents: "auto" }}>
                          {agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ? "Save Controls" : "Save as Default Controls"}
                        </div>
                      );

                      items.push(
                        <DropdownItem
                          key="save-default"
                          className="text-xs rounded-none"
                          isDisabled={isSaveAsDefaultDisabled}
                          classNames={{
                            base: `data-[hover=true]:bg-gray-100 rounded-none ${
                              isSaveAsDefaultDisabled ? "cursor-not-allowed opacity-50" : ""
                            }`,
                          }}
                          onClick={isSaveAsDefaultDisabled ? undefined : (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ? handleOpenSaveControlsModal : handleSaveAsDefault)}
                          textValue="Save as Default Controls"
                        >
                          {isSaveAsDefaultDisabled ? (
                            <ZTooltip
                              content={isReassessmentForSOC ? "Not available in reassessment mode" : "Please fix the errors (marked in red) to continue"}
                              placement="left"
                              showArrow={true}
                              isDisabled={false}
                            >
                              {saveAsDefaultItemContent}
                            </ZTooltip>
                          ) : (
                            saveAsDefaultItemContent
                          )}
                        </DropdownItem>
                      );

                      // Add bulk upload item for non-vendor assessments (enabled in SOC reassessment)
                      if(agentType !== AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT){
                         items.push(
                        <DropdownItem 
                          key="bulk-upload" 
                          className="text-xs rounded-none" 
                          classNames={{
                            base: "data-[hover=true]:bg-gray-100 rounded-none",
                          }}
                          onClick={bulkUploadForSoc2}
                        >
                         <div className="">Bulk Upload Documents</div>
                        </DropdownItem>
                      );
                      }

                      // Add delete item (disabled in SOC reassessment)
                      items.push(
                        <DropdownItem 
                          key="delete" 
                          className="text-danger text-xs rounded-none" 
                          classNames={{
                            base: `text-danger data-[hover=true]:bg-gray-100 data-[hover=true]:text-danger rounded-none ${
                              isReassessmentForSOC ? "cursor-not-allowed opacity-50" : ""
                            }`,
                            description: "text-danger",
                            title: "text-danger",
                          }}
                          isDisabled={isReassessmentForSOC}
                          onClick={isReassessmentForSOC ? undefined : () => {
                            if (!showCheckboxes) {
                              toggleCheckboxes();
                            }
                          }}
                        >
                         <div className="text-danger">Delete Controls</div>
                        </DropdownItem>
                      );
                      
                      return items;
                    })()}
                  </DropdownMenu>
                </Dropdown>
                )}</>
              )}
            </>
          )
        )
      )}
      
      {/* Import Modal for SOC2 agent types */}
      {agentType === AgentSessionType.SOC2_GAP ||
       agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
       agentType === AgentSessionType.SOC2_GAP_TYPE2 ||
       agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ? (
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={handleCloseImportModal}
          onImport={handleImportSuccess}
          sessionId={sessionId || ""}
          isAgentTypeMultiFile={agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT}
          controlSets={existingControlSets} 
          isLoadingControlSets={false} // We're now handling loading state in the parent
        />
      ) : null}
      
      {/* Existing modals */}
      <BulkUploadDocumentsModal
        isOpen={isBulkUploadModalOpen}
        onClose={handleCloseBulkUploadModal}
        onAfterUpload={handleAfterUpload}
        selectedControls={selectedControls}
      />
      
      {/* Save Controls Modal for Multi-Vendor */}
      {agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT && (
        <SaveControlsModal
          isOpen={isSaveControlsModalOpen}
          onClose={handleCloseSaveControlsModal}
          onSave={handleSaveControlSet}
          onNavigateToOverwrite={handleNavigateToOverwrite}
          existingControlSets={existingControlSets.map(set => set.controls_set_name)}
          controlsUrl={currentControlsUrl || undefined}
          prePopulateName={(() => {
            const names = selectedControlSetNames.length > 0 ? selectedControlSetNames : selectedControlSetNamesRef.current;
            return names.length === 1 ? names[0] : undefined;
          })()}
        />
      )}
    </>
  );
};

export default CustomizeControlTopRIghtSection;
